(() => {
  const normalizeText = (value) => (value || '').replace(/\s+/g, ' ').trim();

  if (!customElements.get('buddyora-mobile-checkout-bar')) {
    customElements.define('buddyora-mobile-checkout-bar', class BuddyOraMobileCheckoutBar extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.refreshProductReferences();
        this.button = this.querySelector('[data-mobile-checkout-button]');
        this.status = this.querySelector('[data-mobile-checkout-status]');
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        this.hasSeenTrigger = false;
        this.visibilityMode = this.getVisibilityMode();

        if (!this.purchaseButton || !this.button) return;

        this.button.addEventListener('click', this.handleCheckout);
        this.mobileQuery.addEventListener?.('change', this.handleViewportChange);
        this.desktopQuery.addEventListener?.('change', this.handleViewportChange);
        window.addEventListener('scroll', this.scheduleVisibilitySync, { passive: true });
        window.addEventListener('resize', this.scheduleVisibilitySync, { passive: true });
        window.addEventListener('orientationchange', this.scheduleVisibilitySync, { passive: true });

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (String(data?.sectionId) !== String(this.dataset.sectionId)) return;
            const sourceBar = data?.html?.querySelector(
              `buddyora-mobile-checkout-bar[data-section-id="${this.dataset.sectionId}"]`
            );
            window.requestAnimationFrame(() => {
              this.refreshProductReferences();
              this.syncSelection(sourceBar);
              this.scheduleVisibilitySync();
            });
          });
        }

        this.syncSelection();
        this.scheduleVisibilitySync();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.handleCheckout);
        this.mobileQuery?.removeEventListener?.('change', this.handleViewportChange);
        this.desktopQuery?.removeEventListener?.('change', this.handleViewportChange);
        window.removeEventListener('scroll', this.scheduleVisibilitySync);
        window.removeEventListener('resize', this.scheduleVisibilitySync);
        window.removeEventListener('orientationchange', this.scheduleVisibilitySync);
        this.variantChangeUnsubscriber?.();
        if (this.visibilityFrame) window.cancelAnimationFrame(this.visibilityFrame);
        this.setVisibility(false);
        this.initialized = false;
      }

      getVisibilityMode() {
        if (this.mobileQuery.matches) return 'mobile';
        if (this.desktopQuery.matches) return 'desktop';
        return null;
      }

      refreshProductReferences() {
        this.productInfo = this.closest('product-info');
        this.productForm = this.productInfo?.querySelector(`#${this.dataset.formId}`);
        this.purchaseButton = this.productInfo?.querySelector(`#ProductSubmitButton-${this.dataset.sectionId}`)
          || this.productInfo?.querySelector('.product-form__submit[name="add"]');
      }

      getVisibilityTarget(mode) {
        const packagePicker = this.productInfo?.querySelector('[data-package-picker]');
        return mode === 'desktop' ? packagePicker || this.purchaseButton : this.purchaseButton;
      }

      handleViewportChange = () => {
        const nextMode = this.getVisibilityMode();
        if (nextMode !== this.visibilityMode) {
          this.visibilityMode = nextMode;
          this.hasSeenTrigger = false;
        }
        this.scheduleVisibilitySync();
      };

      scheduleVisibilitySync = () => {
        if (this.visibilityFrame) return;
        this.visibilityFrame = window.requestAnimationFrame(() => {
          this.visibilityFrame = null;
          this.syncVisibility();
        });
      };

      syncVisibility() {
        this.refreshProductReferences();
        const mode = this.getVisibilityMode();
        if (mode !== this.visibilityMode) {
          this.visibilityMode = mode;
          this.hasSeenTrigger = false;
        }
        const visibilityTarget = this.getVisibilityTarget(mode);
        const rect = visibilityTarget?.getBoundingClientRect();
        if (!rect || !mode) {
          this.setVisibility(false);
          return;
        }

        const isVisible = rect.width > 0
          && rect.height > 0
          && rect.bottom > 0
          && rect.top < window.innerHeight
          && rect.right > 0
          && rect.left < window.innerWidth;
        if (isVisible) {
          this.hasSeenTrigger = true;
          if (mode === 'desktop') {
            this.setVisibility(false);
            return;
          }
        }

        if (!this.hasSeenTrigger) {
          this.setVisibility(false);
        } else if (rect.bottom <= -16) {
          this.setVisibility(true, mode);
        } else if (mode === 'desktop' || rect.bottom >= 16) {
          this.setVisibility(false);
        }
      }

      setVisibility(isVisible, mode = this.getVisibilityMode()) {
        const visibilityChanged = this.classList.contains('is-visible') !== isVisible;
        this.classList.toggle('is-visible', isVisible);
        this.setAttribute('aria-hidden', String(!isVisible));
        document.body.classList.toggle('has-buddyora-mobile-checkout-bar', isVisible && mode === 'mobile');
        document.body.classList.toggle('has-buddyora-desktop-checkout-drawer', isVisible && mode === 'desktop');
        if (isVisible && visibilityChanged) {
          this.updateBodyOffset(mode);
        } else {
          if (!isVisible) {
            document.body.style.removeProperty('--buddyora-mobile-checkout-bar-height');
            document.body.style.removeProperty('--buddyora-desktop-checkout-drawer-height');
          }
        }
      }

      updateBodyOffset(mode = this.getVisibilityMode()) {
        window.requestAnimationFrame(() => {
          if (!this.classList.contains('is-visible')) return;
          const property = mode === 'desktop'
            ? '--buddyora-desktop-checkout-drawer-height'
            : '--buddyora-mobile-checkout-bar-height';
          document.body.style.setProperty(property, `${this.offsetHeight}px`);
        });
      }

      syncSelection(sourceBar = null) {
        this.refreshProductReferences();
        const variantInput = this.productInfo?.querySelector('.product-variant-id');
        const packageValue = this.querySelector('[data-mobile-checkout-package]');
        const packageRow = this.querySelector('[data-mobile-checkout-package-row]');
        const colorValue = this.querySelector('[data-mobile-checkout-color]');
        const colorRow = this.querySelector('[data-mobile-checkout-color-row]');
        const price = this.querySelector('[data-mobile-checkout-price]');
        let isAvailable = !this.button.disabled;

        const syncOption = (name, valueElement, rowElement, source = this.productInfo) => {
          if (!name || !valueElement || !rowElement) return;
          const selectedOption = Array.from(source?.querySelectorAll('variant-selects input:checked, variant-selects select option:checked') || [])
            .find((option) => normalizeText(option.dataset.optionName).toLowerCase() === name.toLowerCase());
          const value = selectedOption?.dataset.displayLabel?.trim() || selectedOption?.value?.trim() || '';
          valueElement.textContent = value;
          rowElement.hidden = !value;
        };

        if (variantInput) {
          this.variantId = variantInput.value;
          isAvailable = !variantInput.disabled && !this.productInfo?.querySelector('.product-form__submit')?.disabled;
          syncOption(this.dataset.packageOptionName, packageValue, packageRow);
          syncOption(this.dataset.colorOptionName, colorValue, colorRow);
          const productPrice = this.productInfo?.querySelector(`#price-${this.dataset.sectionId} .price-item--sale, #price-${this.dataset.sectionId} .price-item--regular`);
          if (price && productPrice) price.textContent = productPrice.textContent.trim();
        } else if (sourceBar) {
          this.variantId = sourceBar.dataset.variantId;
          this.dataset.variantId = this.variantId || '';
          const sourcePackage = sourceBar.querySelector('[data-mobile-checkout-package]');
          const sourcePackageRow = sourceBar.querySelector('[data-mobile-checkout-package-row]');
          const sourceColor = sourceBar.querySelector('[data-mobile-checkout-color]');
          const sourceColorRow = sourceBar.querySelector('[data-mobile-checkout-color-row]');
          const sourcePrice = sourceBar.querySelector('[data-mobile-checkout-price]');
          const sourceButton = sourceBar.querySelector('[data-mobile-checkout-button]');
          isAvailable = !sourceButton?.disabled;
          if (packageValue && sourcePackage) packageValue.textContent = sourcePackage.textContent;
          if (packageRow && sourcePackageRow) packageRow.hidden = sourcePackageRow.hidden;
          if (colorValue && sourceColor) colorValue.textContent = sourceColor.textContent;
          if (colorRow && sourceColorRow) colorRow.hidden = sourceColorRow.hidden;
          if (price && sourcePrice) price.textContent = sourcePrice.textContent;
        } else {
          this.variantId = this.dataset.variantId;
        }

        this.button.disabled = !isAvailable;
        this.button.textContent = isAvailable
          ? this.button.dataset.defaultText
          : window.variantStrings?.soldOut || 'Épuisé';
        if (this.classList.contains('is-visible')) this.updateBodyOffset();
      }

      handleCheckout = async () => {
        this.refreshProductReferences();
        if (!this.variantId || this.button.disabled) return;
        const defaultText = this.button.dataset.defaultText || 'Acheter maintenant';
        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');
        this.button.textContent = this.button.dataset.loadingText || 'Ajout…';
        if (this.status) this.status.textContent = '';

        try {
          if (!this.productForm) throw new Error('Formulaire produit indisponible');
          const formSubmit = this.productForm.querySelector('[name="add"]');
          if (!formSubmit) throw new Error('Bouton produit indisponible');
          formSubmit.click();
          window.setTimeout(() => {
            this.button.disabled = false;
            this.button.removeAttribute('aria-busy');
            this.button.textContent = defaultText;
          }, 1200);
        } catch (error) {
          this.button.disabled = false;
          this.button.removeAttribute('aria-busy');
          this.button.textContent = defaultText;
          if (this.status) this.status.textContent = error.message || window.cartStrings?.error || 'Ajout impossible';
        }
      };
    });
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-buddyora-copy-promo]');
    if (!button) return;
    const promo = button.closest('[data-buddyora-promo]');
    const code = promo?.querySelector('[data-buddyora-promo-code]')?.textContent?.trim();
    if (!code) return;
    const defaultLabel = button.dataset.defaultLabel || button.textContent.trim() || 'Copier';
    try {
      await navigator.clipboard.writeText(code);
      button.textContent = 'Copié';
      const status = promo.querySelector('[data-buddyora-promo-status]');
      if (status) status.textContent = 'Code copié';
      window.setTimeout(() => { button.textContent = defaultLabel; }, 1800);
    } catch (_) {
      const range = document.createRange();
      range.selectNodeContents(promo.querySelector('[data-buddyora-promo-code]'));
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      const status = promo.querySelector('[data-buddyora-promo-status]');
      if (status) status.textContent = 'Sélectionnez le code puis copiez-le';
    }
  });

  function shuffle(items) {
    const copy = items.slice();
    for (let index = copy.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
    }
    return copy;
  }

  function getReviewText(review, selectors) {
    for (const selector of selectors) {
      const element = review.querySelector(selector);
      const text = normalizeText(element?.textContent);
      if (text) return text;
    }
    return '';
  }

  function getReviewRating(review) {
    const scoreElement = review.querySelector('[data-score]');
    const score = Number.parseFloat(scoreElement?.dataset.score);
    if (Number.isFinite(score)) return Math.round(score);

    const ratingElement = review.querySelector('.jdgm-rev__rating, [aria-label*="star"], [title*="star"]');
    const ratingText = `${ratingElement?.getAttribute('aria-label') || ''} ${ratingElement?.getAttribute('title') || ''} ${ratingElement?.textContent || ''}`;
    const ratingMatch = ratingText.match(/([1-5])(?:\.0)?\s*(?:star|étoile|\/\s*5)/i);
    if (ratingMatch) return Number.parseInt(ratingMatch[1], 10);

    const activeStars = review.querySelectorAll('.jdgm-star.jdgm--on, .jdgm-star.jdgm--half').length;
    return activeStars || 0;
  }

  function ratingStars(rating) {
    const roundedRating = Math.max(1, Math.min(5, Number.parseInt(rating, 10) || 5));
    return `${'★'.repeat(roundedRating)}${'☆'.repeat(5 - roundedRating)}`;
  }

  function collectJudgeMeReviews() {
    return Array.from(document.querySelectorAll('.jdgm-rev'))
      .map((review) => ({
        title: getReviewText(review, ['.jdgm-rev__title', '.jdgm-rev__header .jdgm-rev__title']),
        body: getReviewText(review, ['.jdgm-rev__body', '.jdgm-rev__content']),
        author: getReviewText(review, ['.jdgm-rev__author', '.jdgm-rev__buyer-badge-wrapper + *', '[class*="author"]']),
        rating: getReviewRating(review),
      }))
      .filter((review) => review.body || review.title);
  }

  function selectReviewHighlights(reviews, count) {
    const fiveStarReviews = shuffle(reviews.filter((review) => review.rating === 5));
    const fourStarReviews = shuffle(reviews.filter((review) => review.rating === 4));
    const otherReviews = shuffle(reviews.filter((review) => review.rating !== 4 && review.rating !== 5));

    if (fiveStarReviews.length >= 4 && fourStarReviews.length >= 1) {
      return shuffle([...fiveStarReviews.slice(0, 4), fourStarReviews[0]]).slice(0, count);
    }

    const selected = [];
    if (fourStarReviews.length) selected.push(fourStarReviews[0]);
    selected.push(...fiveStarReviews.slice(0, Math.max(0, count - selected.length)));
    selected.push(...fourStarReviews.slice(1, Math.max(1, count - selected.length + 1)));
    selected.push(...otherReviews.slice(0, Math.max(0, count - selected.length)));

    return shuffle(selected).slice(0, count);
  }

  function populateReviewHighlights() {
    const container = document.querySelector('[data-buddyora-review-highlights]');
    if (!container || container.dataset.reviewsLoaded === 'true') return false;
    const cards = Array.from(container.querySelectorAll('[data-buddyora-review-card]'));
    if (!cards.length) return false;
    const reviews = selectReviewHighlights(collectJudgeMeReviews(), cards.length);
    if (!reviews.length) return false;

    container.hidden = false;
    cards.forEach((card, index) => {
      const review = reviews[index];
      if (!review) {
        card.hidden = true;
        return;
      }
      card.hidden = false;
      const title = card.querySelector('[data-review-title]');
      const body = card.querySelector('[data-review-body]');
      const author = card.querySelector('[data-review-author]');
      if (title) title.textContent = review.title || 'Avis vérifié';
      if (body) body.textContent = review.body;
      if (author) author.textContent = review.author || 'Client BuddyOra';
      card.dataset.rating = String(review.rating || 5);
      card.dataset.ratingStars = ratingStars(review.rating);
    });

    container.dataset.reviewsLoaded = 'true';
    return true;
  }

  function initReviewHighlights() {
    if (!document.querySelector('[data-buddyora-review-highlights]')) return;
    if (populateReviewHighlights()) return;

    let attempts = 0;
    const maxAttempts = 30;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (populateReviewHighlights() || attempts >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, 500);

    const observer = new MutationObserver(() => {
      if (populateReviewHighlights()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), maxAttempts * 500);
  }

  function detectDynamicPaymentType(root) {
    const text = normalizeText(root?.textContent).toLowerCase();
    const html = (root?.innerHTML || '').toLowerCase();
    const haystack = `${text} ${html}`;
    const candidates = [
      ['paypal', ['paypal', 'pay pal']],
      ['airwallex', ['airwallex', 'air wallex', '空中汇']],
      ['apple-pay', ['apple pay', 'apple_pay', 'apple-pay']],
      ['google-pay', ['google pay', 'google_pay', 'google-pay', 'gpay']],
      ['shopify-pay', ['shop pay', 'shopify pay', 'shopify_pay', 'shopify-pay']],
      ['klarna', ['klarna']],
    ];
    const match = candidates.find(([, aliases]) => aliases.some((alias) => haystack.includes(alias)));
    return match ? match[0] : '';
  }

  function normalizePaymentType(type) {
    const normalized = (type || '').replace(/_/g, '-').toLowerCase();
    if (normalized === 'shop-pay') return 'shopify-pay';
    return normalized;
  }

  function getSupportedPaymentTypes(activeType) {
    const paymentGroups = {
      paypal: ['paypal', 'visa', 'master', 'american-express', 'discover'],
      airwallex: ['visa', 'master', 'american-express', 'apple-pay', 'google-pay', 'unionpay'],
      'apple-pay': ['apple-pay', 'visa', 'master', 'american-express'],
      'google-pay': ['google-pay', 'visa', 'master', 'american-express'],
      'shopify-pay': ['shopify-pay', 'visa', 'master', 'american-express', 'apple-pay', 'google-pay'],
      klarna: ['klarna', 'visa', 'master'],
    };
    return paymentGroups[activeType] || [activeType];
  }

  function syncPaymentMethods() {
    const methods = document.querySelector('[data-buddyora-payment-methods]');
    if (!methods) return false;
    const items = Array.from(methods.querySelectorAll('[data-payment-type]'));
    if (!items.length) {
      methods.hidden = true;
      return true;
    }

    const paymentRoot = methods.previousElementSibling?.matches('product-form')
      ? methods.previousElementSibling.querySelector('.shopify-payment-button')
      : document.querySelector('.shopify-payment-button');
    const activeType = detectDynamicPaymentType(paymentRoot);
    const supportedTypes = activeType ? getSupportedPaymentTypes(activeType) : [];
    const matchedItems = supportedTypes.length
      ? items.filter((item) => supportedTypes.includes(normalizePaymentType(item.dataset.paymentType)))
      : [];

    if (matchedItems.length) {
      items.forEach((item) => { item.hidden = !matchedItems.includes(item); });
      methods.dataset.activePaymentType = activeType;
      methods.classList.add('is-filtered');
    } else {
      const enabledItems = items.filter((item) => item.dataset.paymentEnabled === 'true');
      items.forEach((item) => { item.hidden = enabledItems.length ? !enabledItems.includes(item) : false; });
      methods.dataset.activePaymentType = '';
      methods.classList.remove('is-filtered');
    }

    methods.hidden = false;
    return Boolean(activeType);
  }

  function initPaymentMethods() {
    if (!document.querySelector('[data-buddyora-payment-methods]')) return;
    syncPaymentMethods();

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (syncPaymentMethods() || attempts >= 20) {
        window.clearInterval(timer);
      }
    }, 400);

    const paymentRoot = document.querySelector('.shopify-payment-button');
    if (paymentRoot) {
      const observer = new MutationObserver(syncPaymentMethods);
      observer.observe(paymentRoot, { childList: true, subtree: true, attributes: true });
      window.setTimeout(() => observer.disconnect(), 8000);
    }
  }

  const desktopDescriptionQuery = window.matchMedia('(min-width: 990px)');
  let desktopDescriptionPlacementInitialized = false;
  let productDetailsLinksInitialized = false;

  function syncDesktopProductDescriptions() {
    document.querySelectorAll('.template-product product-info').forEach((productInfo) => {
      const product = productInfo.querySelector('.product');
      const mount = productInfo.querySelector('[data-buddyora-desktop-description]');
      const description = productInfo.querySelector('.product__description');
      if (!product || !mount || !description) return;

      let origin = productInfo.querySelector('[data-buddyora-description-origin]');
      if (!origin && !mount.contains(description)) {
        origin = document.createElement('span');
        origin.hidden = true;
        origin.dataset.buddyoraDescriptionOrigin = '';
        description.before(origin);
      }

      if (desktopDescriptionQuery.matches) {
        if (description.parentElement !== mount) mount.append(description);
      } else if (origin?.parentNode && description.previousElementSibling !== origin) {
        origin.after(description);
      }

      mount.hidden = !desktopDescriptionQuery.matches || !mount.contains(description);
    });
  }

  function initDesktopProductDescriptionPlacement() {
    syncDesktopProductDescriptions();
    if (desktopDescriptionPlacementInitialized) return;
    desktopDescriptionPlacementInitialized = true;

    if (desktopDescriptionQuery.addEventListener) {
      desktopDescriptionQuery.addEventListener('change', syncDesktopProductDescriptions);
    } else {
      desktopDescriptionQuery.addListener(syncDesktopProductDescriptions);
    }
    document.addEventListener('product-info:loaded', syncDesktopProductDescriptions);
  }

  function initProductDetailsLinks() {
    document.querySelectorAll('[data-buddyora-product-details-link]').forEach((link) => {
      if (link.dataset.buddyoraDetailsLinkInitialized) return;
      link.dataset.buddyoraDetailsLinkInitialized = 'true';

      link.addEventListener('click', (event) => {
        const targetId = link.getAttribute('href')?.replace('#', '');
        const target = targetId ? document.getElementById(targetId) : null;
        if (!target || target.hidden) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    if (productDetailsLinksInitialized) return;
    productDetailsLinksInitialized = true;
    document.addEventListener('product-info:loaded', initProductDetailsLinks);
  }

  function initDesktopMediaRailHeight() {
    const galleries = document.querySelectorAll('.template-product media-gallery');

    galleries.forEach((gallery) => {
      const updateHeight = () => {
        const activeMedia = gallery.querySelector('.product__media-item.is-active .product-media-container');
        if (!activeMedia) return;
        const height = Math.ceil(activeMedia.getBoundingClientRect().height);
        if (height > 0) gallery.style.setProperty('--buddyora-main-media-height', `${height}px`);
      };

      const resizeObserver = new ResizeObserver(updateHeight);
      gallery.querySelectorAll('.product__media-item .product-media-container').forEach((media) => resizeObserver.observe(media));

      const mutationObserver = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => mutation.attributeName === 'class')) updateHeight();
      });
      mutationObserver.observe(gallery, { attributes: true, subtree: true, attributeFilter: ['class'] });

      updateHeight();
      window.addEventListener('load', updateHeight, { once: true });
    });
  }

  function initDesktopThumbnailPreview() {
    const desktopQuery = window.matchMedia('(min-width: 990px)');

    document.querySelectorAll('.template-product media-gallery').forEach((gallery) => {
      const thumbnails = gallery.querySelectorAll('[id^="GalleryThumbnails"] [data-target]');
      const galleryImages = gallery.querySelectorAll('.product__media-item .product__modal-opener--image img');
      let requestedMediaId = '';

      galleryImages.forEach((image) => {
        image.loading = 'eager';
      });

      const showWhenReady = (thumbnail) => {
        const mediaId = thumbnail.dataset.target;
        const targetMedia = gallery.querySelector(`[data-media-id="${mediaId}"]`);
        const image = targetMedia?.querySelector('.product__modal-opener--image img');
        if (!image) return;

        requestedMediaId = mediaId;
        image.loading = 'eager';

        const show = () => {
          if (requestedMediaId !== mediaId || !image.naturalWidth) return;
          gallery.setActiveMedia?.(mediaId, false);
        };

        if (image.complete) {
          show();
          return;
        }

        image.addEventListener('load', show, { once: true });
      };

      thumbnails.forEach((thumbnail) => {
        const button = thumbnail.querySelector('button');
        if (!button) return;

        thumbnail.addEventListener('pointerenter', (event) => {
          if (!desktopQuery.matches || event.pointerType === 'touch' || button.hasAttribute('aria-current')) return;
          showWhenReady(thumbnail);
        });

        button.addEventListener('click', (event) => {
          if (!desktopQuery.matches) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          showWhenReady(thumbnail);
        }, true);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initReviewHighlights();
      initPaymentMethods();
      initDesktopProductDescriptionPlacement();
      initProductDetailsLinks();
      initDesktopMediaRailHeight();
      initDesktopThumbnailPreview();
    }, { once: true });
  } else {
    initReviewHighlights();
    initPaymentMethods();
    initDesktopProductDescriptionPlacement();
    initProductDetailsLinks();
    initDesktopMediaRailHeight();
    initDesktopThumbnailPreview();
  }
})();
